import React, { useState, useCallback, useEffect } from 'react';
import * as d3 from 'd3';
import './StackVisualizer.css';

const StackVisualizer = () => {
  const [stack, setStack] = useState([5, 10, 15]);
  const [inputValue, setInputValue] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [actionType, setActionType] = useState('');

  const openModal = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
  };

  const handlePush = () => {
    setActionType('push');
    setShowInput(true);
  };

  const handlePop = () => {
    if (stack.length > 0) {
      const newStack = [...stack];
      newStack.pop();
      setStack(newStack);
      openModal('Top element popped from the stack.');
    } else {
      openModal('Stack is empty!');
    }
  };

  const handlePeek = () => {
    if (stack.length > 0) {
      openModal(`Top of the stack: ${stack[stack.length - 1]}`);
    } else {
      openModal('Stack is empty!');
    }
  };

  const handleChange = () => {
    setActionType('change');
    setShowInput(true);
  };

  const handleSubmit = () => {
    if (inputValue === '') {
      openModal('Please enter a value!');
      return;
    }

    if (actionType === 'push') {
      setStack([...stack, parseInt(inputValue)]);
      openModal(`Value ${inputValue} pushed onto the stack.`);
    } else if (actionType === 'change') {
      if (stack.length > 0) {
        const newStack = [...stack];
        newStack[stack.length - 1] = parseInt(inputValue);
        setStack(newStack);
        openModal(`Top element changed to ${inputValue}.`);
      } else {
        openModal('Stack is empty!');
      }
    }

    setInputValue('');
    setShowInput(false);
  };

  const drawStack = useCallback(() => {
    const svg = d3
      .select('#stack-container')
      .attr('width', 200)
      .attr('height', 400);
    svg.selectAll('*').remove();
  
    const rectHeight = 50; // Height of each box
    const rectSpacing = 10; // Spacing between boxes
  
    // Calculate the starting Y position for the stack
    const totalHeight = stack.length * (rectHeight + rectSpacing);
    const startY = 400 - totalHeight;
  
    // Draw rectangles (boxes)
    svg
      .selectAll('rect')
      .data(stack)
      .enter()
      .append('rect')
      .attr('x', 50)
      .attr('y', (d, i) => startY + i * (rectHeight + rectSpacing)) // Stack from the top
      .attr('width', 100)
      .attr('height', rectHeight)
      .attr('fill', '#81c784')
      .attr('stroke', 'black')
      .attr('stroke-width', 2)
      .attr('rx', 10);
  
    // Draw text (numbers)
    svg
      .selectAll('text')
      .data(stack)
      .enter()
      .append('text')
      .attr('x', 100)
      .attr('y', (d, i) => startY + i * (rectHeight + rectSpacing) + rectHeight / 2 + 5) // Center text in boxes
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('font-size', '16px')
      .attr('font-weight', 'bold')
      .text((d) => d);
  }, [stack]);
  

  useEffect(() => {
    drawStack();
  }, [drawStack]);

  return (
    <div className="stack-visualizer-container">
      <h2>Stack Visualizer</h2>
      <svg id="stack-container"></svg>

      <div className="controls">
        <button onClick={handlePush}>Push</button>
        <button onClick={handlePop}>Pop</button>
        <button onClick={handlePeek}>Peek</button>
        <button onClick={handleChange}>Change Top</button>

        {showInput && (
          <div className="input-container">
            <input
              type="number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Enter value"
            />
            <button onClick={handleSubmit}>Submit</button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal" onClick={(e) => e.target.className === 'modal' && closeModal()}>
          <div className="modal-content">
            <span className="close" onClick={closeModal}>
              &times;
            </span>
            <p>{modalMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StackVisualizer;
